import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search,
  Loader2,
  Upload,
  CheckCircle2,
} from "lucide-react";
import api from "../../../chat/api";
import toast from "react-hot-toast";
import { getFileIcon, getTagStyle } from "../utils/fileIcons";

const SelectFormProjectModal = ({ isOpen, onClose, folderId, onImport }) => {
  const navigate = useNavigate();
  const { projectId, orgId } = useParams();
  const [loading, setLoading] = useState(false);
  const [filesData, setFilesData] = useState({ documents: [], folder_name: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  // 'idle' | 'processing' | 'success'
  const [importStatus, setImportStatus] = useState("idle");

  const fetchFiles = useCallback(async () => {
    if (!folderId) return;

    setLoading(true);
    try {
      const filesUrl = orgId
        ? `api/v1/rfp/folders/${folderId}/files/?organization_id=${encodeURIComponent(orgId)}`
        : `api/v1/rfp/folders/${folderId}/files/`;
      const response = await api.get(filesUrl);

      if (response.status === 200) {
        const data = response.data;

        const mappedFiles = {
          documents:
            data.documents
              ?.map((doc) => ({
                id: doc.id,
                name: doc.name || "Untitled",
                uploaded_at: doc.uploaded_at,
                size: doc.size || "0 KB",
                type: doc.type || "-",
                url: doc.url,
                key: doc.key,
                file_type: doc.file_type || doc.name?.split(".").pop(),
                status: doc.status || "ready",
              }))
              // autopdf only supports PDFs
              .filter(
                (doc) =>
                  (doc.file_type || "").toLowerCase() === "pdf" ||
                  doc.name?.toLowerCase().endsWith(".pdf")
              ) || [],
          folder_name: data.folder_name,
        };

        setFilesData(mappedFiles);
      } else {
        setFilesData({ documents: [], folder_name: "N/A" });
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      const status = error.response?.status;
      const apiError = error.response?.data?.error;
      if ((status === 400 || status === 403) && apiError) {
        toast.error(apiError);
      } else {
        toast.error("Failed to load project files");
      }
      setFilesData({ documents: [], folder_name: "N/A" });
    } finally {
      setLoading(false);
    }
  }, [folderId, orgId]);

  useEffect(() => {
    if (isOpen) {
      fetchFiles();
      setSelectedFile(null);
      setSearchQuery("");
      setImportStatus("idle");
    }
  }, [isOpen, fetchFiles]);

  const handleImportClick = async () => {
    if (!selectedFile) return;

    const pdfKey = selectedFile.key;
    if (!pdfKey) {
      toast.error("Selected file is missing a pdf key.");
      return;
    }

    try {
      // Modular flow: process only creates the job (status `created`) and
      // probes the form strategy. The user picks Manual / AI on the review
      // page; AI annotation runs on demand there.
      setImportStatus("processing");
      const processRes = await api.post(
        `/api/v1/rfp/autopdf/folders/${folderId}/documents/${pdfKey}/process/`,
        {},
        { timeout: 0 }
      );

      if (![200, 201].includes(processRes.status)) {
        throw new Error("Process step failed");
      }

      const processedStatus = processRes.data?.status;
      // `created` (new/reset job) or any already-in-progress status is fine —
      // the review page drives the rest of the flow.
      const OK_STATUSES = ["created", "ready_for_review", "extracting_fields", "completed"];
      if (!OK_STATUSES.includes(processedStatus)) {
        throw new Error(
          `Process step returned status "${processedStatus || "unknown"}".`
        );
      }

      const jobId = processRes.data?.job_id;
      if (!jobId) throw new Error("No job_id returned by process step");

      const pdfUrl = processRes.data?.pdf_url || "";
      const name = processRes.data?.name || selectedFile.name;

      setImportStatus("success");
      toast.success("Form opened.");

      // Hand off to the review page. Pass pdf_url + name through state so the
      // page can render immediately while it fetches the field bboxes.
      const reviewPath = orgId
        ? `/rfp-projects/project/${projectId}/org/${orgId}/forms/review/${jobId}`
        : `/rfp-projects/project/${projectId}/forms/review/${jobId}`;

      onImport && onImport();
      onClose();
      setImportStatus("idle");
      navigate(reviewPath, { state: { pdfUrl, name } });
      return;
    } catch (error) {
      console.error("Form import failed:", error);
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fill form. Please try again.";
      toast.error(msg);
      setImportStatus("idle");
    }
  };

  const filteredFiles = filesData.documents.filter((doc) =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  const isWorking = importStatus !== "idle" && importStatus !== "success";

  const buttonLabel = (() => {
    switch (importStatus) {
      case "processing":
        return "Processing form fields...";
      case "success":
        return "Ready for Review";
      default:
        return "Import & Process";
    }
  })();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-[1px] animate-in fade-in duration-200">
      <div
        className="bg-white rounded-xl shadow-2xl w-full font-outfit max-w-[650px] flex flex-col animate-in zoom-in-95 duration-200 border border-gray-100"
        style={{ height: "600px", maxHeight: "70vh" }}
      >
        {/* --- Header --- */}
        <div className="px-8 pt-6 pb-2">
          <div className="flex justify-between items-start mb-1">
            <div>
              <h2 className="text-[18px] font-semibold text-[#18101C] leading-tight">
                {filesData.folder_name || "Project Files"}
              </h2>
              <p className="text-[14px] text-[#52525B] font-normal mt-1">
                Select a PDF form ({selectedFile ? "1" : "0"}/1 Selected)
              </p>
            </div>

            <div className="relative w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md !text-[14px] !leading-5 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* --- Column Headers --- */}
        <div className="px-8 mt-2">
          <div className="border-b border-[#0000001A] pb-3 flex">
            <span className="text-[14px] text-[#00000080] font-medium w-[75%]">
              Document Name
            </span>
            <span className="text-[14px] text-[#00000080] font-medium w-[25%]">
              Tags
            </span>
          </div>
        </div>

        {/* --- File List --- */}
        <div className="flex-1 overflow-y-auto px-4 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-[#C95EBE]" />
              <span className="text-sm">Loading files...</span>
            </div>
          ) : filteredFiles.length > 0 ? (
            <div className="py-2 space-y-2">
              {filteredFiles.map((file) => {
                const isSelected = selectedFile?.id === file.id;
                return (
                  <div
                    key={file.id}
                    onClick={() => {
                      if (importStatus === "idle") {
                        setSelectedFile(isSelected ? null : file);
                      }
                    }}
                    className={`relative flex items-center px-2 py-2 rounded-lg cursor-pointer transition-all border ${
                      isSelected
                        ? "bg-[#fdf2f8] border-[#f0abfc] shadow-sm"
                        : "bg-white border-transparent hover:bg-gray-50"
                    } ${
                      importStatus !== "idle"
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    <div className="w-[75%] pl-3 flex items-center gap-3 pr-4">
                      <div className="shrink-0">
                        {getFileIcon(file.file_type)}
                      </div>
                      <span className="text-[14px] text-gray-700 font-medium truncate">
                        {file.name}
                      </span>
                    </div>

                    <div className="w-[25%] flex items-center justify-between">
                      <span
                        className={`p-4 py-1 rounded-full text-[12px] font-medium border ${getTagStyle(
                          file.type
                        )}`}
                      >
                        {file.type || "RFP"}
                      </span>

                      {isSelected && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M8.6 11.8L6.45 9.65C6.26667 9.46667 6.03333 9.375 5.75 9.375C5.46667 9.375 5.23333 9.46667 5.05 9.65C4.86667 9.83333 4.775 10.0667 4.775 10.35C4.775 10.6333 4.86667 10.8667 5.05 11.05L7.9 13.9C8.1 14.1 8.33333 14.2 8.6 14.2C8.86667 14.2 9.1 14.1 9.3 13.9L14.95 8.25C15.1333 8.06667 15.225 7.83333 15.225 7.55C15.225 7.26667 15.1333 7.03333 14.95 6.85C14.7667 6.66667 14.5333 6.575 14.25 6.575C13.9667 6.575 13.7333 6.66667 13.55 6.85L8.6 11.8ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                            fill="#34C759"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
              No PDF documents found
            </div>
          )}
        </div>

        {/* --- Footer --- */}
        <div className="py-5 flex justify-center">
          <button
            disabled={!selectedFile || isWorking}
            onClick={handleImportClick}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-[14px] font-medium transition-all shadow-sm ${
              selectedFile
                ? importStatus === "success"
                  ? "bg-green-600 text-white border border-green-600"
                  : "bg-[#C95EBE] text-white border border-[#C95EBE]"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isWorking ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {buttonLabel}
              </>
            ) : importStatus === "success" ? (
              <>
                <CheckCircle2 size={16} />
                {buttonLabel}
              </>
            ) : (
              <>
                <Upload size={16} />
                {buttonLabel}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Click outside to close (disabled while working) */}
      <div
        className="absolute inset-0 -z-10"
        onClick={() => {
          if (importStatus === "idle") onClose();
        }}
      ></div>
    </div>
  );
};

export default SelectFormProjectModal;
